/**
 * ============================================================
 * KEJJA OS — OnboardingCelebrationPage (v3 — emoji confetti)
 * ============================================================
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, ArrowRight, Sparkles } from 'lucide-react';

// Kejja-themed emojis — family, money, home automation, community
const EMOJIS = [
  '🏠', '🏡', '👨‍👩‍👧', '👨‍👩‍👦', '👩‍👧', '👨‍👧',  // homes + family
  '💰', '💵', '💳', '📈', '🪙',                        // money
  '🍲', '🥘', '🍚', '🥗', '🍎',                        // meals
  '💡', '🕯️', '📅', '🛋️', '🛏️',                     // home items
  '🛒', '🧺', '📦',                                     // pantry / shopping
  '🤝', '❤️', '⭐', '🎯', '✨',                          // community / goals
];

export default function OnboardingCelebrationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const homeName = (location.state as { homeName?: string } | null)?.homeName ?? 'your home';

  const [items] = useState(() =>
    Array.from({ length: 26 }, (_, i) => ({
      id: i,
      emoji:    EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
      left:     Math.random() * 96 + 2,
      delay:    Math.random() * 2,
      duration: 6 + Math.random() * 5,
      size:     22 + Math.random() * 18,
      swing:    Math.random() * 24 - 12,
      rotate:   Math.random() * 40 - 20,
    })),
  );

  useEffect(() => {
    const t = setTimeout(() => navigate('/dashboard', { replace: true }), 12_000);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <main className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#0e415e] via-[#196287] to-[#2563eb] text-white">
      {/* Floating emojis */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {items.map((it) => (
          <div
            key={it.id}
            className="absolute select-none"
            style={{
              left:      `${it.left}%`,
              bottom:    '-80px',
              fontSize:  `${it.size}px`,
              animation: `floatUp ${it.duration}s linear ${it.delay}s infinite, spin ${3 + Math.random() * 4}s ease-in-out ${it.delay}s infinite alternate`,
              filter:    'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
              transform: `rotate(${it.rotate}deg)`,
            }}
          >
            {it.emoji}
          </div>
        ))}
      </div>

      {/* Sparkles layer */}
      <div className="absolute inset-0 pointer-events-none opacity-60" aria-hidden>
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              top:       `${Math.random() * 100}%`,
              left:      `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="h-28 w-28 rounded-3xl bg-white/10 backdrop-blur-md border-4 border-white/50 shadow-2xl flex items-center justify-center animate-[popIn_0.7s_cubic-bezier(0.34,1.56,0.64,1)_forwards]">
          <Home className="w-14 h-14 text-white" strokeWidth={2.2} />
        </div>

        <div className="mt-10 space-y-4 animate-[fadeInUp_0.9s_ease-out_0.5s_both] max-w-xl">
          <div className="flex items-center justify-center gap-2 text-white/75 text-xs uppercase tracking-[0.3em] font-semibold">
            <Sparkles className="w-3 h-3" /> Welcome home <Sparkles className="w-3 h-3" />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight drop-shadow-lg">{homeName}</h1>
          <p className="text-white/90 text-base md:text-lg max-w-md mx-auto">
            Your family OS is ready. Money, meals, pantry, routines, and community — all in one place.
          </p>
        </div>

        <button
          onClick={() => navigate('/dashboard', { replace: true })}
          className="mt-10 h-12 px-8 bg-white text-[#0e415e] rounded-xl font-bold text-base shadow-2xl hover:scale-105 transition-transform flex items-center gap-2 animate-[fadeInUp_0.9s_ease-out_1s_both]"
        >
          Enter home
          <ArrowRight className="w-5 h-5" />
        </button>

        <p className="mt-4 text-white/60 text-xs animate-[fadeInUp_0.9s_ease-out_1.4s_both]">
          Or sit back — we'll take you in automatically.
        </p>
      </div>

      <style>{`
        @keyframes floatUp {
          0%   { transform: translateY(0)       rotate(0deg); }
          100% { transform: translateY(-120vh)  rotate(360deg); }
        }
        @keyframes spin {
          0%   { margin-left: -10px; }
          100% { margin-left: 10px; }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0; }
          50%      { opacity: 1; }
        }
        @keyframes popIn {
          0%   { transform: scale(0) rotate(-180deg); opacity: 0; }
          70%  { transform: scale(1.1) rotate(0deg);  opacity: 1; }
          100% { transform: scale(1) rotate(0deg);    opacity: 1; }
        }
        @keyframes fadeInUp {
          0%   { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </main>
  );
}
